// App.js
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import Category from "./components/Category/Category";
import FloatingControls from "./components/FloatingControls";
import { MealsProvider } from "./components/Meal/MealsContext";
import Checkout from "./components/Order/Checkout";
import Order from "./components/Order/Order";
import OrderConfirmation from "./components/OrderConfirmation";
import Package from "./components/Package/Package";
import { ConfigProvider } from "./config/ConfigContext";
import { getSubdomain } from "./config/configUtils";

function App() {
  const [visibleSection, setVisibleSection] = useState("food");

  const handleScroll = () => {
    const foodPosition = document
      .getElementById("food")
      ?.getBoundingClientRect();
    const drinksPosition = document
      .getElementById("drink")
      ?.getBoundingClientRect();
    const orderPosition = document
      .getElementById("order")
      ?.getBoundingClientRect();
    const packagePosition = document
      .getElementById("package")
      ?.getBoundingClientRect();

    const windowHeight = window.innerHeight;

    // Determine if the top of the drinks section is visible
    const isDrinksVisible = drinksPosition
      ? drinksPosition.top < windowHeight && drinksPosition.bottom > 0
      : false;

    const isPackageVisible = packagePosition
      ? packagePosition.top < windowHeight && packagePosition.bottom > 0
      : false;

    // Determine if the top of the order section is visible
    const isOrderVisible = orderPosition
      ? orderPosition.top < windowHeight && orderPosition.bottom > 0
      : false;

    // Determine if the top of the food section is visible
    const isFoodVisible = foodPosition
      ? foodPosition.top < windowHeight && foodPosition.bottom > 0
      : false;

    if (isOrderVisible) {
      setVisibleSection("order");
    } else if (isDrinksVisible) {
      setVisibleSection("drink");
    } else if (isPackageVisible) {
      setVisibleSection("package");
    } else if (isFoodVisible) {
      setVisibleSection("food");
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const subdomain = getSubdomain();

  return (
    <div className="min-h-screen font-sans">
      <header className="sticky top-0">
        <h1 className="text-3xl font-bold hidden">Catering Order</h1>
      </header>

      <FloatingControls
        visibleSection={visibleSection}
        scrollToSection={(id) => {
          const element = document.getElementById(id);
          if (element) {
            const offset = -100; // Décalage de -20px
            const elementPosition =
              element.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({
              top: elementPosition + offset,
              behavior: "smooth",
            });
          }
          setVisibleSection(id);
        }}
      />

      <main>
        <div id="food">
          <div className="relative w-screen overflow-hidden">
            <img
              src="/assets/img/food-menu.webp"
              alt="Food Menu"
              className="w-full h-auto object-cover transform origin-center scale-150 sm:scale-100 mb-12"
            />
          </div>

          <div className="mb-12">
            <Category name="Breakfast - 2 pax" />
          </div>
          {/* Row with two categories */}
          <div className="grid grid-cols-1 md:grid-cols-2 mb-12 gap-12">
            <Category name="Starters - 2 pax" />
            <Category name="Sandwiches - 2 pax" />
          </div>

          {/* Full-width category */}
          <div className="mb-12">
            <Category name="Caviar" />
          </div>

          {/* Two-part split category */}
          <div className="mb-12">
            <Category name="Snack Trays - 2 pax" twocols={true} />
          </div>

          {/* Row with two categories */}
          <div className="mb-12">
            <Category name="Main Courses - Individual" twocols={true} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 mb-12 gap-12">
            <Category name="Desserts - To share" />

            <Category name="Desserts - Individual" />
          </div>
        </div>
        <div id="package">
          <div className="relative w-screen overflow-hidden">
            <img
              src="/assets/img/package-menu.webp"
              alt="Package Menu"
              className="w-full h-auto object-cover transform origin-center scale-150 sm:scale-100 mb-12"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 mb-12 gap-12">
            <Package name="Package - 2 Pax" />
            <Package name="Package - 4 Pax" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 mb-12 gap-12">
            <Package name="Package - 6 Pax" />
            <Package name="Package - 8 Pax" />
          </div>
        </div>

        {subdomain !== "h1g" && (
          <div id="drink">
            <div className="relative w-screen overflow-hidden">
              <img
                src="/assets/img/drinks-menu.webp"
                alt="Drinks Menu"
                className="w-full h-auto object-cover transform origin-center scale-150 sm:scale-100 mb-12"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 mb-12 gap-12">
              <Category name="Water" />
              <Category name="Fruit Juice" />
            </div>

            <div className="mb-12">
              <Category name="Soft Drinks" />
            </div>

            {/* Row with two categories */}

            <div className="grid grid-cols-1 md:grid-cols-2 mb-12 gap-12">
              <Category name="Wine - Rose" />
              <Category name="Wine - White" />
            </div>
            <div className="mb-12">
              <Category name="Wine - Red" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 mb-12 gap-12">
              <Category name="Champagne - Brut" />
              <Category name="Champagne - Rose" />
            </div>
            <div className="mb-12">
              <Category name="Champagne - Blanc de Blancs" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 mb-12 gap-12">
              <Category name="Beers" />
              <Category name="Liquor" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 mb-12 gap-12">
              <Category name="Tequila" />

              <Category name="Vodka" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 mb-12 gap-12">
              <Category name="Rhum" />
              <Category name="Gin" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 mb-12 gap-12">
              <Category name="Whisky" />
              <Category name="Cognac" />
            </div>
          </div>
        )}

        <Order />

        <footer className="sticky bottom-0 mt-5">
          <div className=" bg-primary text-center text-white text-sm italic p-1">
            {visibleSection === "food" || visibleSection === "package" ? (
              <div className="flex justify-around">
                <span>All our dishes are homemade</span>
                <span className="flex items-center">
                  <img
                    src="/assets/img/veggie-icon.png"
                    width="15"
                    height="15"
                    alt="Vegetarian"
                    className="mr-1"
                  />
                  Vegetarian dishes
                </span>
              </div>
            ) : (
              "For last minute requests, please note that some bottles may not be available"
            )}
          </div>
        </footer>
      </main>
    </div>
  );
}

export default function AppWrapper() {
  return (
    <ConfigProvider>
      <MealsProvider>
        <BrowserRouter>
          <Helmet>
            <title>Order Catering</title>
          </Helmet>
          <Routes>
            <Route path="/" element={<App />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/confirmation" element={<OrderConfirmation />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </BrowserRouter>
      </MealsProvider>
    </ConfigProvider>
  );
}
